"use client";

import { useState, useEffect, useCallback } from "react";
import { Info } from "lucide-react";
import { useMembershipGate } from "./membership/MembershipGate";

const ACK_KEY = "cjowl-dashboard-disclaimer-ack";

/**
 * One-time honesty disclaimer for the CJ Owl member dashboard: the savings
 * estimates, scores, opportunities, recommendations and service statuses are
 * illustrative SAMPLE data until a member's real numbers are wired in and
 * reviewed by their Savings Advisor.
 *
 * Shown as a lightweight modal (so it doesn't take up dashboard space like the
 * old inline banner) and acknowledged once — the ack is persisted in localStorage
 * so it never nags again. Mounted once in the (dashboard) layout, so it covers
 * every tab on both desktop and mobile. Starts hidden to avoid a hydration
 * mismatch (localStorage is client-only).
 */
export function DashboardDisclaimerModal() {
  const [open, setOpen] = useState(false);
  // The membership page comes first for a Basic member (client, 2026-09-18); this follows once it is closed.
  const { upsellOpen } = useMembershipGate();

  useEffect(() => {
    if (localStorage.getItem(ACK_KEY) !== "1") setOpen(true);
  }, []);

  const acknowledge = useCallback(() => {
    try { localStorage.setItem(ACK_KEY, "1"); } catch {}
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") acknowledge(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, acknowledge]);

  if (!open || upsellOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cjowl-disclaimer-title"
    >
      <div className="fixed inset-0 bg-navy-900/60 backdrop-blur-sm" onClick={acknowledge} />

      <div className="relative w-full max-w-md my-auto rounded-2xl bg-white shadow-2xl border border-lavender-200">
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 shrink-0">
              <Info className="h-5 w-5" />
            </span>
            <h2 id="cjowl-disclaimer-title" className="text-lg font-extrabold text-navy-500 leading-tight">
              A quick note before you explore
            </h2>
          </div>

          <p className="mt-4 text-[13.5px] leading-relaxed text-gray-600">
            The savings estimates, scores, opportunities, recommendations, and service
            statuses shown are{" "}
            <span className="font-semibold text-navy-500">sample data for demonstration purposes only</span>.
            Your personalized dashboard will be populated with real insights after your
            profile, properties, and household services have been fully set up and reviewed
            by your Savings Advisor.
          </p>

          <button
            onClick={acknowledge}
            autoFocus
            className="mt-5 w-full rounded-xl bg-cj-800 hover:bg-cj-900 text-white text-sm font-bold px-5 py-3 transition-colors"
          >
            I understand
          </button>
        </div>
      </div>
    </div>
  );
}
